import React, { useState } from 'react';
import FullBlockingModal from '../common/FullBlockingModal';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import PrioritySelector from './PrioritySelector';
import { useListingTypeSelector } from '../../hooks/useListingTypeSelector';
import { ListingType } from '../../types/job';

interface EditPriorityModalProps {
  listingType?: ListingType | null;
  onClose: () => void;
  onUpdate: (listingTypeId: number) => void;
}

const EditPriorityModal: React.FC<EditPriorityModalProps> = ({
  listingType,
  onClose,
  onUpdate,
}) => {
  const { t } = useTranslation(['jobs', 'common']);
  const { options, selectedOption, setSelectedOption } = useListingTypeSelector(listingType);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const handleUpdateListingType = () => {
    if (selectedOption) {
      onUpdate(selectedOption.id);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[72rem]">
      <h2 className="text-2xl md:text-3xl font-bold text-secondary mb-6">
        {t('change_priority_button')}
      </h2>

      <div className="border-t pt-6 mt-4">
        <div className="text-xl font-bold text-secondary mb-4">{t('publish.priority_title')}</div>

        <PrioritySelector
          priorities={options}
          selectedPriority={selectedOption}
          setSelectedPriority={setSelectedOption}
          error={errors.priority}
          clearError={() => setErrors(prev => ({ ...prev, priority: undefined }))}
        />

        <div className="flex flex-col md:flex-row justify-end gap-4 mt-6">
          <Button
            className="underline text-secondary font-semibold px-4 py-2"
            onClick={onClose}
            type="button"
            variant="alternate"
          >
            {t('publish.cancel')}
          </Button>

          <Button
            onClick={handleUpdateListingType}
            type="button"
            disabled={!selectedOption || selectedOption.id === listingType?.id}
            variant="primary"
            className="px-8 py-2 w-auto"
          >
            {selectedOption
              ? t('publish.use_credits_and_modify', { count: selectedOption.cost })
              : t('publish.use_credits_and_modify', { count: 1 })}
          </Button>
        </div>
      </div>

      {!selectedOption && (
        <>
          <p className="mt-10 text-sm text-secondary font-bold mb-1">
            {t('wallet', { ns: 'jobs' })}
          </p>
          <div className="w-full mt-2 border border-secondary-light rounded-full px-6 py-3 flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">{t('credits', { ns: 'jobs' })}</span>
                <span className="text-2xl font-bold text-slate-700">0</span>
              </div>
            </div>
            <Button className="w-1/5 text-primary-inverse font-semibold py-1 hover:bg-slate-800 transition">
              {t('manage', { ns: 'jobs' })}
            </Button>
          </div>
        </>
      )}
    </FullBlockingModal>
  );
};

export default EditPriorityModal;
